import { SITE_NAME, absoluteUrl, getCanonicalOrigin } from './seoConfig';

/** Shared meta description for landing + JSON-LD WebSite */
export const LANDING_META_DESCRIPTION =
  'JobMockAI helps recruiters hire faster with AI-powered resume matching, helps candidates prepare for interviews, and enables experienced professionals to earn as interviewers.';

/**
 * JSON-LD `JobPosting` for individual job detail pages (/apply/:id).
 *
 * This is what enables Google Jobs rich results — the special job cards
 * that appear at the top of Google Search when someone searches for a job title.
 *
 * Required by Google:
 *   title, description, hiringOrganization, datePosted, jobLocation OR
 *   applicantLocationRequirements (for remote), directApply, url
 *
 * @param {object} job  — raw job row from the API
 */
export function buildJobPostingJsonLd(job) {
  if (!job) return null;

  const origin = getCanonicalOrigin().replace(/\/$/, '');
  const jobUrl = absoluteUrl(`/apply/${job.id}`);
  const datePosted = job.published_at || job.created_at
    ? new Date(job.published_at || job.created_at).toISOString().split('T')[0]
    : undefined;
  const validThrough = job.expires_at
    ? new Date(job.expires_at).toISOString().split('T')[0]
    : undefined;

  // Map our location_type to schema.org EmploymentType + location fields
  const employmentTypeMap = {
    full_time: 'FULL_TIME',
    part_time: 'PART_TIME',
    contract: 'CONTRACTOR',
    internship: 'INTERN',
    freelance: 'TEMPORARY',
  };
  const employmentType = employmentTypeMap[job.role_type] || 'FULL_TIME';

  const isRemote = job.location_type === 'remote' || job.location_type === 'hybrid';

  // Salary
  const baseSalary = (job.salary_min || job.salary_max)
    ? {
        '@type': 'MonetaryAmount',
        currency: 'USD',
        value: {
          '@type': 'QuantitativeValue',
          ...(job.salary_min ? { minValue: job.salary_min } : {}),
          ...(job.salary_max ? { maxValue: job.salary_max } : {}),
          unitText: 'YEAR',
        },
      }
    : undefined;

  return {
    '@context': 'https://schema.org',
    '@type': 'JobPosting',
    title: job.title,
    description: job.description || `${job.title} opportunity at ${job.company_name || SITE_NAME}.`,
    url: jobUrl,
    ...(datePosted ? { datePosted } : {}),
    ...(validThrough ? { validThrough } : {}),
    employmentType,
    directApply: true,
    hiringOrganization: {
      '@type': 'Organization',
      name: job.company_name || SITE_NAME,
      sameAs: job.company_website || `${origin}/jobs/company/${job.company_slug || ''}`,
      ...(job.company_logo ? { logo: job.company_logo } : {}),
    },
    // Remote jobs get applicantLocationRequirements; others get jobLocation
    ...(isRemote
      ? {
          applicantLocationRequirements: { '@type': 'Country', name: 'Worldwide' },
          jobLocationType: 'TELECOMMUTE',
        }
      : {
          jobLocation: {
            '@type': 'Place',
            address: {
              '@type': 'PostalAddress',
              addressLocality: job.location || 'United States',
              addressCountry: 'US',
            },
          },
        }),
    ...(baseSalary ? { baseSalary } : {}),
    ...(Array.isArray(job.required_skills) && job.required_skills.length > 0
      ? { skills: job.required_skills.join(', ') }
      : {}),
    ...(job.experience_level
      ? { experienceRequirements: { '@type': 'OccupationalExperienceRequirements', monthsOfExperience: 0, description: job.experience_level } }
      : {}),
  };
}

/**
 * JSON-LD for the marketing landing route (/landing).
 */
export function buildLandingJsonLd() {
  const origin = getCanonicalOrigin().replace(/\/$/, '');
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebSite',
        '@id': `${origin}/#website`,
        name: SITE_NAME,
        url: origin,
        description: LANDING_META_DESCRIPTION,
      },
      {
        '@type': 'Organization',
        '@id': `${origin}/#organization`,
        name: SITE_NAME,
        url: origin,
        logo: absoluteUrl('/icon-512x512.svg'),
      },
    ],
  };
}

/**
 * JSON-LD for SEO content list/hub pages.
 */
export function buildWebPageJsonLd({ name, description, path }) {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': absoluteUrl(path),
        name,
        description,
        url: absoluteUrl(path),
        isPartOf: { '@type': 'WebSite', name: SITE_NAME, url: getCanonicalOrigin().replace(/\/$/, '') },
      },
    ],
  };
}

/**
 * JSON-LD Article + BreadcrumbList for /blog, /guides, /interview-questions detail routes.
 */
export function buildContentArticleJsonLd({ item, basePath, sectionLabel }) {
  const url = absoluteUrl(`${basePath}/${item.slug}`);
  const origin = getCanonicalOrigin().replace(/\/$/, '');
  const pub = item.created_at ? new Date(item.created_at).toISOString() : undefined;
  const mod = item.updated_at ? new Date(item.updated_at).toISOString() : pub;

  const article = {
    '@type': 'Article',
    '@id': `${url}#article`,
    headline: item.title,
    ...(item.summary || item.meta_description
      ? { description: item.meta_description || item.summary }
      : {}),
    ...(pub ? { datePublished: pub } : {}),
    ...(mod ? { dateModified: mod } : {}),
    author: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: origin,
    },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: origin,
      logo: {
        '@type': 'ImageObject',
        url: absoluteUrl('/icon-512x512.svg'),
      },
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    url,
  };

  const breadcrumb = {
    '@type': 'BreadcrumbList',
    '@id': `${url}#breadcrumb`,
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${origin}/landing` },
      { '@type': 'ListItem', position: 2, name: sectionLabel, item: absoluteUrl(basePath) },
      { '@type': 'ListItem', position: 3, name: item.title, item: url },
    ],
  };

  return { '@context': 'https://schema.org', '@graph': [article, breadcrumb] };
}
